import { useEffect, useRef, useState } from 'react'
import DOMPurify from 'dompurify'
import Button from '@/components/ui/Button'
import Loading from '@/components/shared/Loading'
import { useAuth } from '@/auth'
import appConfig from '@/configs/app.config'
import {
    CONSENT_ACCEPTED_COUNT_KEY,
    CONSENT_REQUIRED_KEY,
    CONSENT_TOTAL_KEY,
} from '@/constants/app.constant'
import {
    apiGetPendingConsents,
    apiSubmitConsent,
    type PendingConsent,
} from '@/services/ConsentService'

const getFirstAccessiblePath = (): string => {
    try {
        const menu = localStorage.getItem('userMenuList')
        const userMenuList = menu ? JSON.parse(menu) : []

        const allUrlPaths: string[] = userMenuList.flatMap((main: any) => {
            const urls: string[] = []

            if (main.urlPath) {
                urls.push(main.urlPath)
            }

            if (Array.isArray(main.menuList)) {
                main.menuList.forEach((sub: any) => {
                    if (sub.urlPath) {
                        urls.push(sub.urlPath)
                    }
                })
            }

            return urls
        })

        return allUrlPaths[0] || appConfig.authenticatedEntryPath1
    } catch (error) {
        return appConfig.authenticatedEntryPath1
    }
}

const ConsentForm = () => {
    const { signOut } = useAuth()

    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [pendingConsents, setPendingConsents] = useState<PendingConsent[]>([])
    const [acceptedCount, setAcceptedCount] = useState(() => {
        const stored = Number(sessionStorage.getItem(CONSENT_ACCEPTED_COUNT_KEY))
        return Number.isFinite(stored) && stored > 0 ? stored : 0
    })
    const [knownTotal, setKnownTotal] = useState(() => {
        const stored = Number(sessionStorage.getItem(CONSENT_TOTAL_KEY))
        return Number.isFinite(stored) && stored > 0 ? stored : 0
    })
    const [rejected, setRejected] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)

    const totalConsents = Math.max(
        knownTotal,
        acceptedCount + pendingConsents.length,
    )
    const currentPageNumber = Math.min(acceptedCount + 1, totalConsents)

    const empCode = localStorage.getItem('empCode') || ''
    const contentRef = useRef<HTMLDivElement>(null)

    const checkScrolledToBottom = () => {
        const el = contentRef.current
        if (!el) {
            return
        }
        const reachedBottom =
            el.scrollHeight - el.scrollTop - el.clientHeight <= 2
        if (reachedBottom) {
            setHasScrolledToBottom(true)
        }
    }

    const proceedPastConsent = () => {
        sessionStorage.removeItem(CONSENT_REQUIRED_KEY)
        sessionStorage.removeItem(CONSENT_ACCEPTED_COUNT_KEY)
        sessionStorage.removeItem(CONSENT_TOTAL_KEY)

        const targetPath = getFirstAccessiblePath()
        const normalizedPath = targetPath.startsWith('/')
            ? targetPath
            : `/${targetPath}`

        // Hard navigation so route guards, navigation config (which reads
        // localStorage at module init), and the router state all re-evaluate
        // against the now-cleared consent flag. A soft navigate() alone left
        // the consent screen mounted in some cases.
        window.location.href = `/portal${normalizedPath}`
    }

    useEffect(() => {
        const loadPendingConsents = async () => {
            if (!empCode) {
                setLoading(false)
                return
            }

            const pending = await apiGetPendingConsents(empCode)

            if (pending.length === 0) {
                proceedPastConsent()
                return
            }

            setPendingConsents(pending)
            setLoading(false)
        }

        loadPendingConsents()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const currentConsent = pendingConsents[0]

    useEffect(() => {
        sessionStorage.setItem(
            CONSENT_ACCEPTED_COUNT_KEY,
            String(acceptedCount),
        )
    }, [acceptedCount])

    useEffect(() => {
        const runningTotal = acceptedCount + pendingConsents.length
        if (runningTotal > knownTotal) {
            setKnownTotal(runningTotal)
            sessionStorage.setItem(CONSENT_TOTAL_KEY, String(runningTotal))
        }
    }, [acceptedCount, pendingConsents.length, knownTotal])

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0
        }
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'auto' })
        }
        setHasScrolledToBottom(false)
        requestAnimationFrame(() => {
            const el = contentRef.current
            if (el && el.scrollHeight <= el.clientHeight + 2) {
                setHasScrolledToBottom(true)
            }
        })
    }, [currentConsent?.consentVersionId])

    const handleAccept = async () => {
        if (!currentConsent) {
            return
        }

        setSubmitting(true)
        setErrorMessage('')

        const result = await apiSubmitConsent(
            empCode,
            currentConsent.consentVersionId,
            'ACCEPTED',
        )

        setSubmitting(false)

        if (!result.success) {
            setErrorMessage(result.message)
            return
        }

        setRejected(false)

        const remaining =
            result.pendingConsents.length > 0
                ? result.pendingConsents.filter(
                      (c) =>
                          c.consentVersionId !== currentConsent.consentVersionId,
                  )
                : pendingConsents.filter(
                      (c) =>
                          c.consentVersionId !== currentConsent.consentVersionId,
                  )

        if (remaining.length === 0) {
            proceedPastConsent()
            return
        }

        setAcceptedCount((prev) => prev + 1)
        setPendingConsents(remaining)
    }

    const handleReject = async () => {
        if (!currentConsent) {
            return
        }

        setSubmitting(true)
        setErrorMessage('')

        const result = await apiSubmitConsent(
            empCode,
            currentConsent.consentVersionId,
            'REJECTED',
        )

        setSubmitting(false)

        if (!result.success) {
            setErrorMessage(result.message)
            return
        }

        setRejected(true)
    }

    if (loading) {
        return (
            <div className="w-full flex-1 min-h-0 flex items-center justify-center px-3 sm:px-6 md:px-8 lg:px-12 py-6">
                <Loading loading />
            </div>
        )
    }

    if (!empCode || !currentConsent) {
        return (
            <div className="w-full flex-1 min-h-0 flex flex-col items-center justify-center text-center px-3 sm:px-6 md:px-8 lg:px-12 py-6">
                <p className="text-red-500 text-sm sm:text-base">
                    Unable to load your pending consents. Please sign in again.
                </p>
                <Button className="mt-3 sm:mt-4" onClick={() => signOut()}>
                    Log Out
                </Button>
            </div>
        )
    }

    if (rejected) {
        return (
            <div className="w-full flex-1 min-h-0 flex flex-col justify-center px-3 sm:px-6 md:px-8 lg:px-12 py-4 sm:py-6 overflow-y-auto">
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 sm:p-4 mb-3 sm:mb-4">
                    <p className="text-red-600 font-semibold text-center text-sm sm:text-base">
                        You cannot use the portal without accepting{' '}
                        {currentConsent.consentTitle}.
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-3">
                    <Button onClick={() => setRejected(false)}>
                        Review Again
                    </Button>
                    <Button variant="solid" onClick={() => signOut()}>
                        Log Out
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="w-full flex flex-col flex-1 min-h-0 px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12 min-[2560px]:px-20 py-2 sm:py-2 md:py-3 lg:py-3 xl:py-4 2xl:py-5 min-[2560px]:py-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2 mb-2 md:mb-2 lg:mb-3 xl:mb-3 2xl:mb-4 min-[2560px]:mb-6 shrink-0">
                <h5 className="font-semibold text-gray-800 dark:text-gray-100 text-xs sm:text-sm md:text-sm lg:text-base xl:text-base 2xl:text-lg min-[2560px]:text-2xl truncate">
                    {currentConsent.consentTitle}
                </h5>
                {totalConsents >= 1 && (
                    <span className="inline-flex items-center self-start sm:self-auto shrink-0 whitespace-nowrap px-2 xl:px-3 min-[2560px]:px-4 py-0.5 sm:py-1 xl:py-1.5 min-[2560px]:py-2 rounded-full text-[10px] sm:text-[11px] xl:text-xs min-[2560px]:text-base font-medium bg-blue-50 text-blue-600 border border-blue-100">
                        Page {currentPageNumber} of {totalConsents}
                    </span>
                )}
            </div>
            <div
                ref={contentRef}
                onScroll={checkScrolledToBottom}
                className="flex-1 min-h-[80px] sm:min-h-[100px] md:min-h-[120px] lg:min-h-[140px] xl:min-h-[160px] 2xl:min-h-[180px] min-[2560px]:min-h-[360px] border border-gray-200 dark:border-gray-700 rounded-lg xl:rounded-xl min-[2560px]:rounded-2xl p-3 sm:p-3 md:p-4 lg:p-4 xl:p-5 2xl:p-6 min-[2560px]:p-10 overflow-y-auto text-[11px] sm:text-xs md:text-xs lg:text-sm xl:text-sm 2xl:text-sm min-[2560px]:text-xl leading-relaxed bg-gray-50/50 dark:bg-gray-900/40 consent-content break-words"
                dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(currentConsent.consentText),
                }}
            />

            {errorMessage && (
                <p className="mt-2 sm:mt-2 xl:mt-3 text-center text-red-500 text-xs sm:text-sm xl:text-base min-[2560px]:text-xl shrink-0">{errorMessage}</p>
            )}

            <div className="mt-2 md:mt-3 lg:mt-3 xl:mt-4 2xl:mt-4 min-[2560px]:mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 xl:gap-4 min-[2560px]:gap-6 pt-2 md:pt-3 lg:pt-3 xl:pt-4 2xl:pt-4 min-[2560px]:pt-8 border-t border-gray-100 dark:border-gray-700 shrink-0">
                <Button
                    className="w-full sm:w-auto sm:min-w-[100px] md:min-w-[120px] xl:min-w-[140px] min-[2560px]:min-w-[220px] xl:text-base min-[2560px]:text-xl min-[2560px]:py-3"
                    disabled={submitting}
                    onClick={handleReject}
                >
                    Reject
                </Button>
                <Button
                    className="w-full sm:w-auto sm:min-w-[100px] md:min-w-[120px] xl:min-w-[140px] min-[2560px]:min-w-[220px] xl:text-base min-[2560px]:text-xl min-[2560px]:py-3"
                    variant="solid"
                    disabled={submitting || !hasScrolledToBottom}
                    onClick={handleAccept}
                >
                    {submitting ? 'Submitting...' : 'Accept'}
                </Button>
            </div>
        </div>
    )
}

export default ConsentForm
